import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    ActionTableCell,
    CheckboxTableRow,
    CopyTableButton,
    ExTable,
    SelectionFixedFooter,
    TableBody,
    TableCell,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import { IssueRequestForm, useDownloadCsvInstitutionsApi, useFetchInstitutionsApi, useIssueRequestFormApi } from 'api/institution'
import { Institution, InstitutionSearchForm } from 'entities/institution'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { GlobalStateContext } from 'contexts/global_state_context'
import { institutionColumnInfoList, InstitutionTableData } from './institution_table'
import { TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import qs from 'qs'
import { InputField } from 'components/shared/input'
import { useState } from 'react'
import { EditInstitutionForm, NewInstitutionForm } from './institution_form'
import { Flex } from 'components/shared/flex'
import { Button } from 'react-bootstrap'
import { CreateMaterialTaskForm } from 'api/task_set'
import { MasterDataContext } from 'contexts/master_data_context'
import { CreateMaterialTaskModal } from 'components/task_set/create_material_task_modal'
import { AddressViewModel } from 'view_models/address_view_model'
import moment from 'moment'
import { downloadFile } from 'utils/util'
import Encoding from 'encoding-japanese'
import { homeInstitutionColumnInfoList, HomeInstitutionTableData } from './institution_table_home'

type Props = {
    history: H.History
}

const InstitutionsPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const masterData = useContext(MasterDataContext)
    const params = useQuery<InstitutionSearchForm>()
    const preSearchForm = useForm<InstitutionSearchForm>(params)
    const searchForm = useForm<InstitutionSearchForm>({ ordering: 'institution_id', ...params })
    const api = useFetchInstitutionsApi(searchForm)
    const downloadCsvApi = useDownloadCsvInstitutionsApi(searchForm)
    const multipleCheckForm = useMultipleCheckForm<Institution>()

    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST
    const institutionDisplaySetting = useDisplaySetting(
        isHomeTest ? homeInstitutionColumnInfoList(searchForm) : institutionColumnInfoList(searchForm),
        'Institution'
    )
    const [selectedInstitution, setSelectedInstitution] = useState<Institution>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const addRequestForm = useForm<IssueRequestForm>({})
    const issueRequestApi = useIssueRequestFormApi()
    const [showAddRequest, setShowAddRequest] = useState<boolean>(false)
    const [hoveredItem, setHoveredItem] = useState<Institution>({})
    let timeoutId: NodeJS.Timeout

    const createTasksForm = useForm<CreateMaterialTaskForm>({})

    useEffect(() => {
        api.execute()
    }, [])

    const handleClickRemoveFilter = (key: keyof InstitutionSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`institutions?${queryParams}`)
        }
    }, [searchForm.object])

    const handleClickEdit = (institution: Institution) => {
        setSelectedInstitution(institution)
        setShowEdit(true)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(downloadCsvApi.loading)
    }, [downloadCsvApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(issueRequestApi.loading)
        if (issueRequestApi.isSuccess()) {
            api.execute()
            setShowAddRequest(false)
        }
    }, [issueRequestApi.loading])

    useEffectSkipFirst(() => {
        if (multipleCheckForm.form.object.items.length > 0) {
            createTasksForm.update((f) => {
                f.institutions = multipleCheckForm.form.object.items
            })
        }
    }, [multipleCheckForm.form.object.items])

    return (
        <>
            <EditInstitutionForm
                institution={selectedInstitution}
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    api.execute()
                    setShowEdit(false)
                }}
            />

            <ContentHeader
                headerTitle={isHomeTest ? '提携先一覧' : '病院・クリニック'}
                left={
                    <>
                        <CopyTableButton />
                        <NewInstitutionForm
                            onComplete={() => {
                                api.execute()
                            }}
                        />
                        <SearchConditionView searchForm={searchForm} onClickRemoveButton={handleClickRemoveFilter} />
                    </>
                }
                right={<>{/* <DisplaySettingModal displaySettings={displaySettings} /> */}</>}
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    <InputField
                        attr="name"
                        form={preSearchForm}
                        placeholder="施設名"
                        onPressEnter={() => {
                            searchForm.updateObject('name', preSearchForm.getValue('name'))
                        }}
                        style={{ marginBottom: 0, width: 250 }}
                    />
                </Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={multipleCheckForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow head allCheck allItems={api.response.results} multipleCheckForm={multipleCheckForm} showEdit>
                                <ActionTableCell th width={60} />
                                <TableHeads displaySetting={institutionDisplaySetting} />
                                {isHomeTest && <TableCell th width={120} align={'center'} />}
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={institutionDisplaySetting.getColumnLength() + 3}>
                            {api.response.results.map((institution: Institution) => {
                                return (
                                    <CheckboxTableRow
                                        key={`institution-${institution.id}`}
                                        item={institution}
                                        multipleCheckForm={multipleCheckForm}
                                        onClickEdit={() => handleClickEdit(institution)}
                                        onMouseEnter={() => {
                                            setHoveredItem(institution)
                                            clearTimeout(timeoutId)
                                        }}
                                        onMouseLeave={() => {
                                            clearTimeout(timeoutId)
                                            timeoutId = setTimeout(() => {
                                                setHoveredItem({})
                                            }, 100)
                                        }}
                                    >
                                        <ActionTableCell
                                            action="edit"
                                            onClick={() => {
                                                setSelectedInstitution(institution)
                                                setShowEdit(true)
                                            }}
                                        />
                                        {isHomeTest ? (
                                            <>
                                                <HomeInstitutionTableData
                                                    institution={institution}
                                                    displaySetting={institutionDisplaySetting}
                                                    hovered={hoveredItem.id == institution.id}
                                                    onClickRequestForm={() => {
                                                        props.history.push(
                                                            `/request_form_issuances?institutionId=${institution.id}&amount=${institution.scanSendFormCount}&toC=${institution.toC}`
                                                        )
                                                    }}
                                                />

                                                <TableCell align="center">
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => {
                                                            props.history.push(`home_products?institutionId=${institution.id}`)
                                                        }}
                                                    >
                                                        商品一覧
                                                    </Button>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <InstitutionTableData
                                                institution={institution}
                                                displaySetting={institutionDisplaySetting}
                                                hovered={hoveredItem.id == institution.id}
                                                onClickRequestForm={() => {
                                                    props.history.push(
                                                        `/request_form_issuances?institutionId=${institution.id}&institutionName=${institution.name}&amount=${institution.scanSendFormCount}`
                                                    )
                                                }}
                                            />
                                        )}
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={multipleCheckForm.ids()}
                onClickReset={() => multipleCheckForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="id"
                tableName="Institution"
            >
                <CreateMaterialTaskModal
                    createTasksForm={createTasksForm}
                    useTaskMaster
                    onComplete={() => {
                        multipleCheckForm.reset()
                    }}
                />
                <Button
                    variant="primary"
                    onClick={() => {
                        props.history.push(`/materials?institutionId=${multipleCheckForm.ids().join(',')}`)
                    }}
                >
                    資材管理へ
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => {
                        props.history.push(`/analytics_samples?institutionId=${multipleCheckForm.ids().join(',')}`)
                    }}
                >
                    検体一覧へ
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => {
                        const contents = [
                            '企業名,部署名,担当者名,購入企業ID,郵便番号,住所,電話番号,メールアドレス,FAX番号,請求書送付方法,口座依頼,予備項目2,予備項目3,予備項目4,予備項目5',
                        ]
                        multipleCheckForm.form.object.items.map((item) => {
                            const address = item.address!
                            const vm = new AddressViewModel(address)
                            const line = `${item.name},,${item.personInCharge || ''},${item.code},${address.zipCode},${vm.whole({
                                showZipCode: false,
                            })},${item.tel},,,,有り,,,,`
                            contents.push(line)
                        })
                        const strCSV = contents.join('\n')
                        const codes = Encoding.stringToCode(strCSV)
                        downloadFile(
                            new Uint8Array(Encoding.convert(codes, { to: 'SJIS', from: 'UNICODE' })),
                            `np_import_${moment().format('YYYYMMDDHHmm')}.csv`,
                            'text/plane'
                        )
                    }}
                >
                    NP CSV
                </Button>
            </SelectionFixedFooter>
        </>
    )
}

export default withRouter(InstitutionsPage)
